import {
  Room,
  RoomEvent,
  Track,
  type RemoteTrack,
  type RemoteTrackPublication,
  type RemoteParticipant,
  type TranscriptionSegment,
  type Participant,
} from "livekit-client";
import { CallEvent, CallEventAdapter, CallEventType, QueryType } from "../types";
import { TurnAccumulator } from "../lib/transcriptTurns";

type Handler = (event: CallEvent) => void;

export interface NuplayCallConfig {
  queryType:     QueryType;
  channel:       "phone" | "web";
  agentId:       string;
  phoneNumber?:  string;          // caller's number (phone channel)
  transferPhone?: string;         // warm-transfer destination
  testerEmail?: string;           // tester's own email ("" = none); sent as tester_email only when set
  workspaceId?:  string;          // override workspace-id header (e.g. Copa workspace)
  apiBase?:      string;          // override REACT_APP_NURIX_API_BASE
  wsBase?:       string;          // override REACT_APP_NURIX_WS_BASE
  agentNameOverrides?: Record<string, string>; // agentId -> friendly name (routing-strip backstop)
}

// ─── Agent IDs from env ────────────────────────────────────────────────────────

const AGENT_IDS: Record<QueryType, string> = {
  "wismo":        process.env.REACT_APP_AGENT_ID_WISMO        ?? "be866840-3acf-4d38-a457-7a321a2f5788",
  "cancellation": process.env.REACT_APP_AGENT_ID_CANCELLATION ?? "be866840-3acf-4d38-a457-7a321a2f5788",
  "store-desk":   process.env.REACT_APP_AGENT_ID_STORE_DESK   ?? "be866840-3acf-4d38-a457-7a321a2f5788",
  "product-rec":  process.env.REACT_APP_AGENT_ID_PRODUCT_REC  ?? "be866840-3acf-4d38-a457-7a321a2f5788",
  // Airline queries resolve to the live COPA Crew (also set per-agent on AgentConfig.agentId).
  "refund-status":         process.env.REACT_APP_COPA_AGENT_ID ?? "4c3a3587-bc59-4729-a255-592b4399ddb3",
  "baggage-reimbursement": process.env.REACT_APP_COPA_AGENT_ID ?? "4c3a3587-bc59-4729-a255-592b4399ddb3",
  // BFSI / Fintech queries resolve to Claire (also set per-agent on AgentConfig.agentId).
  "returned-payment":      process.env.REACT_APP_FINTECH_AGENT_ID ?? "FINTECH_AGENT_ID_PLACEHOLDER",
  "compliance-hold":       process.env.REACT_APP_FINTECH_AGENT_ID ?? "FINTECH_AGENT_ID_PLACEHOLDER",
  "missing-invoice":       process.env.REACT_APP_FINTECH_AGENT_ID ?? "FINTECH_AGENT_ID_PLACEHOLDER",
  "integration-sync":      process.env.REACT_APP_FINTECH_AGENT_ID ?? "FINTECH_AGENT_ID_PLACEHOLDER",
};

export function agentIdForQuery(q: QueryType): string {
  return AGENT_IDS[q];
}

// ─── Upstream NuPlay HTTP ──────────────────────────────────────────────────────

// CRA has no server, so the upstream NuPlay call API is hit directly from the
// browser. workspace-id was a server-only secret in the Next.js version; on
// CRA it has to ship to the client. Keep it scoped to a low-risk demo
// workspace and rotate it if it leaks.
const NURIX_API_BASE = process.env.REACT_APP_NURIX_API_BASE   ?? "https://external-proxy-us.nurixlabs.tech";
const NURIX_WS_BASE  = process.env.REACT_APP_NURIX_WS_BASE     ?? "wss://external-proxy-us.nurixlabs.tech";
const WORKSPACE_ID   = process.env.REACT_APP_NURIX_WORKSPACE_ID ?? "e3d23869-60c0-4191-8852-08e67de83a39";

const uuidv4 = () => crypto.randomUUID();

function normalizeSpeaker(raw: unknown): "agent" | "user" | "system" {
  const s = String(raw ?? "").toLowerCase();
  if (s === "user" || s === "customer" || s === "caller") return "user";
  if (s === "system" || s === "tool") return "system";
  return "agent";
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function recordOrWrapped(value: unknown, fallback: Record<string, unknown> = {}): Record<string, unknown> {
  const record = asRecord(value);
  if (record) return record;
  if (value === undefined || value === null || value === "") return fallback;
  return { value };
}

function firstString(...values: unknown[]): string | null {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
}

function firstNumber(...values: unknown[]): number {
  for (const value of values) {
    const num = typeof value === "number" ? value : Number(value);
    if (Number.isFinite(num)) return num;
  }
  return 0;
}

function isErrorValue(...values: unknown[]): boolean {
  for (const value of values) {
    if (typeof value === "boolean") return value;
    if (typeof value === "string") {
      const s = value.toLowerCase();
      if (s === "error" || s === "failed" || s === "failure") return true;
      if (s === "ok" || s === "success" || s === "completed") return false;
    }
    if (value && typeof value === "object") return true;
  }
  return false;
}

function findToolCallSource(msg: Record<string, unknown>, topic?: string): Record<string, unknown> | null {
  const candidates = [
    { record: msg, nestedTool: false },
    { record: asRecord(msg.tool_call), nestedTool: true },
    { record: asRecord(msg.toolCall), nestedTool: true },
    { record: asRecord(msg.tool), nestedTool: true },
    { record: asRecord(msg.payload), nestedTool: false },
    { record: asRecord(msg.data), nestedTool: false },
    { record: asRecord(msg.event), nestedTool: false },
  ].filter((candidate): candidate is { record: Record<string, unknown>; nestedTool: boolean } => Boolean(candidate.record));

  for (const { record: candidate, nestedTool } of candidates) {
    const type = String(candidate.type ?? msg.type ?? topic ?? "").toLowerCase();
    const typeIsTool = type.includes("tool_call") || type.includes("function_call");
    const hasStrongToolName = firstString(
      candidate.function_name,
      candidate.functionName,
      candidate.tool_name,
      candidate.toolName,
    );
    const hasGenericName = firstString(candidate.name);
    const hasToolPayload = [
      candidate.args,
      candidate.arguments,
      candidate.input,
      candidate.parameters,
      candidate.output,
      candidate.response,
      candidate.result,
    ].some((value) => value !== undefined && value !== null);

    if (
      hasStrongToolName ||
      ((typeIsTool || nestedTool) && (hasStrongToolName || hasGenericName)) ||
      (hasGenericName && hasToolPayload)
    ) {
      return candidate;
    }
  }

  return null;
}

function normalizeToolCallEvent(msg: Record<string, unknown>, topic?: string): Extract<CallEvent, { type: "tool_call" }> | null {
  const source = findToolCallSource(msg, topic);
  if (!source) return null;

  const functionName = firstString(
    source.function_name,
    source.functionName,
    source.tool_name,
    source.toolName,
    source.name,
    msg.function_name,
    msg.functionName,
  );
  if (!functionName) return null;

  const nestedResult = asRecord(source.result);
  return {
    type:         "tool_call",
    functionName,
    args:         recordOrWrapped(source.args ?? source.arguments ?? source.input ?? source.parameters ?? msg.args ?? msg.arguments),
    output:       recordOrWrapped(source.output ?? source.response ?? source.result ?? nestedResult?.output ?? msg.output ?? msg.result),
    isError:      isErrorValue(source.is_error, source.isError, source.error, source.status, nestedResult?.error, msg.is_error, msg.isError),
    durationMs:   firstNumber(source.duration_ms, source.durationMs, source.latency_ms, source.elapsed_ms, msg.duration_ms, msg.durationMs),
    timestampMs:  Date.now(),
  };
}

// ─── Adapter ──────────────────────────────────────────────────────────────────

export class NuplayCallAdapter implements CallEventAdapter {
  private handlers: Map<CallEventType, Set<Handler>> = new Map();
  private ws: WebSocket | null = null;
  private room: Room | null = null;
  private subscribedTracks: RemoteTrack[] = [];
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private fallbackTimer: ReturnType<typeof setTimeout> | null = null;
  private callId: string | null = null;
  private remoteCallId: string | null = null;
  private alive = true;
  // True from the first successful connect() until disconnect(). Guards
  // against duplicate calls being placed when the owning React effect re-runs
  // (e.g. hot-module-replacement in dev).
  private connectStarted = false;
  // Collapses the two transcript feeds into one bubble per spoken turn; the
  // why and the containment rules live in lib/transcriptTurns.ts.
  private turns = new TurnAccumulator();
  // Active-agent tracking for multi-agent crews. voiceX publishes the active
  // sub-agent's id on every handoff — V1 squads via a data packet on the
  // "agent_update" topic, V2 squads via the agent participant's metadata. To
  // render friendly names + routing conditions (instead of raw UUIDs) we load
  // the squad's sub-agents + edges once and resolve against them. Same signal
  // zero's crew build page tracks via ParticipantAgentTracker.
  private agentNames: Map<string, string> = new Map();
  private edgeConditions: Map<string, string> = new Map();
  private lastAgentId: string | null = null;
  private squadDetailLoaded = false;
  private pendingAgentIds: string[] = [];

  // Per-instance targeting: Copa overrides the workspace (and optionally base
  // URLs) via AgentConfig; Shopify omits them and falls back to the env globals.
  private readonly apiBase: string;
  private readonly wsBase: string;
  private readonly workspaceId: string;

  constructor(private config: NuplayCallConfig) {
    this.apiBase = config.apiBase ?? NURIX_API_BASE;
    this.wsBase = config.wsBase ?? NURIX_WS_BASE;
    this.workspaceId = config.workspaceId ?? WORKSPACE_ID;
    // Seed friendly agent names so the routing strip renders even if
    // /squad/{id}/detail is unreachable for this workspace.
    if (config.agentNameOverrides) {
      for (const [id, name] of Object.entries(config.agentNameOverrides)) {
        this.agentNames.set(id, name);
      }
    }
  }

  // ── Public ─────────────────────────────────────────────────────────────────

  async connect(sessionCallId: string): Promise<void> {
    // Idempotent — silently ignore duplicate connect attempts (HMR re-runs of
    // the owning React effect, retry loops, etc). A genuinely new call uses a
    // fresh adapter instance.
    if (this.connectStarted || !this.alive) return;
    this.connectStarted = true;

    this.callId = sessionCallId;
    this.emit({ type: "call.connecting" });

    if (this.config.channel === "phone") {
      await this.initiatePhoneCall();
    } else {
      await this.initiateWebCall();
    }
  }

  disconnect(): void {
    this.alive = false;

    // Stop the local mic and detach every remote audio element. Without this,
    // the auto-attached <audio> tags from track.attach() can keep playing the
    // agent's voice after room.disconnect() returns.
    for (const t of this.subscribedTracks) {
      try {
        t.detach().forEach((el) => el.remove());
      } catch {
        // best-effort
      }
    }
    this.subscribedTracks = [];

    try { this.room?.localParticipant.setMicrophoneEnabled(false); } catch {}
    try { this.room?.disconnect(); } catch {}
    this.room = null;

    // Tell the NuPlay backend to hang up the phone leg (web/LiveKit hangs up via
    // room.disconnect, but a phone call lives entirely server-side and would
    // otherwise keep talking to the user's handset).
    if (this.config.channel === "phone" && this.remoteCallId) {
      const id = this.remoteCallId;
      this.remoteCallId = null;
      fetch(`${this.apiBase}/voice/calls/${encodeURIComponent(id)}/end`, {
        method: "POST",
        headers: { "accept": "application/json", "workspace-id": this.workspaceId },
      }).catch(() => {});
    }

    this.ws?.close();
    this.ws = null;

    if (this.pollTimer) clearInterval(this.pollTimer);
    this.pollTimer = null;
    if (this.fallbackTimer) clearTimeout(this.fallbackTimer);
    this.fallbackTimer = null;
    this.handlers.clear();
  }

  setMuted(muted: boolean): void {
    this.room?.localParticipant.setMicrophoneEnabled(!muted).catch(() => {});
  }

  on<T extends CallEvent>(eventType: T["type"], handler: (event: T) => void): void {
    if (!this.handlers.has(eventType)) this.handlers.set(eventType, new Set());
    this.handlers.get(eventType)!.add(handler as Handler);
  }

  off<T extends CallEvent>(eventType: T["type"], handler: (event: T) => void): void {
    this.handlers.get(eventType)?.delete(handler as Handler);
  }

  // The page's "your email" box (TSC). Always sent when the page offers the box, "" when it's empty, so the crew's
  // {{tester_email}} placeholder is filled either way.
  private testerEmailVar(): { tester_email?: string } {
    return this.config.testerEmail === undefined ? {} : { tester_email: this.config.testerEmail };
  }

  // ── Phone call ─────────────────────────────────────────────────────────────

  private async initiatePhoneCall(): Promise<void> {
    if (!this.config.phoneNumber) {
      this.emit({ type: "error", message: "No phone number provided." });
      return;
    }

    try {
      const res = await fetch(`${this.apiBase}/voice/outbound-call`, {
        method:  "POST",
        headers: {
          "Content-Type": "application/json",
          "accept":       "application/json",
          "workspace-id": this.workspaceId,
        },
        body: JSON.stringify({
          agent_id: this.config.agentId,
          number:   this.config.phoneNumber,
          overide_previous_context: true,
          custom_dynamic_variables_config: {
            ...(this.config.transferPhone ? { transfer_phone_number: this.config.transferPhone } : {}),
            ...this.testerEmailVar(),
          },
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.message ?? err?.error ?? `Call initiation failed (${res.status})`);
      }

      const data = await res.json();
      const remoteCallId = data?.call_id ?? data?.id ?? this.callId;
      this.remoteCallId = remoteCallId as string;

      // Open transcription WebSocket — emits call.connected on open.
      // Fallback: if WS hasn't connected within 6 s, signal connected anyway.
      this.openTranscriptSocket(remoteCallId as string);
      this.fallbackTimer = setTimeout(() => {
        if (!this.alive || this.ws?.readyState === WebSocket.OPEN) return;
        this.emit({ type: "call.connected", startedAt: Date.now() });
      }, 6000);

    } catch (err) {
      const msg = err instanceof Error ? err.message : "Could not start the call.";
      this.emit({ type: "error", message: msg });
    }
  }

  // ── Web call (LiveKit) ─────────────────────────────────────────────────────

  private async initiateWebCall(): Promise<void> {
    // Load the crew's sub-agents + edges so live agent_id updates can be shown
    // with friendly names + routing conditions. Fire-and-forget — updates that
    // arrive before it resolves are buffered and flushed in order.
    void this.loadSquadDetail();

    try {
      const res = await fetch(`${this.apiBase}/voice/web/call`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "accept":       "application/json",
          "user-id":      uuidv4(),
          "workspace-id": this.workspaceId,
        },
        body: JSON.stringify({
          agent_id: this.config.agentId,
          overide_previous_context: false,
          custom_dynamic_variables_config: {
            transfer_phone_number: this.config.transferPhone ?? "",
            ...this.testerEmailVar(),
          },
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail ?? err?.error ?? `Web call failed (${res.status})`);
      }

      const { serverUrl, participantToken } = await res.json();

      const room = new Room();
      this.room = room;

      room.on(RoomEvent.Connected, async () => {
        if (!this.alive) return;
        // Go Live immediately. Enabling the mic must NOT block this — a slow or
        // denied mic permission would otherwise strand the call on "connecting"
        // and the agent's audio would never surface. The agent can still be
        // heard one-way; the caller can grant the mic and retry to talk back.
        this.emit({ type: "call.connected", startedAt: Date.now() });
        try {
          await room.localParticipant.setMicrophoneEnabled(true);
        } catch (e) {
          console.warn("[nurixAdapter] mic enable failed — caller won't be heard until permission is granted", e);
        }
      });

      room.on(RoomEvent.Disconnected, () => {
        if (!this.alive) return;
        this.emit({ type: "call.ended", reason: "agent_hangup", endedAt: Date.now() });
      });

      room.on(
        RoomEvent.TrackSubscribed,
        (track: RemoteTrack, _pub: RemoteTrackPublication, _participant: RemoteParticipant) => {
          if (track.kind === Track.Kind.Audio) {
            track.attach();
            this.subscribedTracks.push(track);
          }
        },
      );

      room.on(
        RoomEvent.TrackUnsubscribed,
        (track: RemoteTrack) => {
          try {
            track.detach().forEach((el) => el.remove());
          } catch {
            // best-effort
          }
          this.subscribedTracks = this.subscribedTracks.filter((t) => t !== track);
        },
      );

      room.on(RoomEvent.DataReceived, (payload: Uint8Array, _participant?: RemoteParticipant, _kind?: unknown, topic?: string) => {
        if (!this.alive) return;
        const text = new TextDecoder().decode(payload);
        // V1 squad workers publish the active sub-agent on the "agent_update"
        // topic as {"agent_id": "..."}. Route it to the agent tracker instead
        // of the transcript path (it has no transcript text).
        if (topic === "agent_update") {
          try {
            const parsed = JSON.parse(text);
            if (parsed?.agent_id) this.handleAgentId(String(parsed.agent_id));
          } catch {
            // ignore malformed agent_update frames
          }
          return;
        }
        try {
          const parsed = JSON.parse(text);
          this.handleServerMessage(parsed, topic);
        } catch {
          // Voicex sometimes pushes raw text frames for agent transcripts
          // (no JSON envelope). Treat those as agent turns — same convention
          // as test-2/components/voice/VoicexCallInterface.tsx.
          const trimmed = text.trim();
          if (trimmed) {
            this.emitTranscriptTurn({
              speaker:     "agent",
              text:        trimmed,
              timestampMs: Date.now(),
            });
          }
        }
      });

      // livekit-client v2 native transcription channel. Crew/voiceX agents
      // emit transcripts here instead of on DataChannel, so without this
      // handler the transcript panel stays empty even though the call audio
      // works. Speaker is inferred from the participant identity (local =
      // user, remote = agent). Only `final` segments are surfaced — partial
      // segments would duplicate every spoken word.
      room.on(
        RoomEvent.TranscriptionReceived,
        (segments: TranscriptionSegment[], participant?: Participant) => {
          if (!this.alive) return;
          const isAgent =
            !!participant &&
            participant.identity !== room.localParticipant.identity;
          const speaker = isAgent ? "agent" : "user";
          for (const seg of segments) {
            if (seg.final && seg.text.trim()) {
              this.emitTranscriptTurn({
                speaker,
                text:        seg.text.trim(),
                timestampMs: Date.now(),
              });
            }
          }
        },
      );

      // V2 squad workers write the active agent_id onto the agent participant's
      // metadata ({"agent_id": "..."}) rather than a data packet. Track both
      // metadata changes and any participant already present (initial agent),
      // mirroring zero's ParticipantAgentTracker.
      room.on(
        RoomEvent.ParticipantMetadataChanged,
        (_prev: string | undefined, participant: Participant) => {
          if (!this.alive) return;
          const id = this.extractAgentIdFromMetadata(participant.metadata);
          if (id) this.handleAgentId(id);
        },
      );
      room.on(RoomEvent.ParticipantConnected, (participant: RemoteParticipant) => {
        if (!this.alive) return;
        const id = this.extractAgentIdFromMetadata(participant.metadata);
        if (id) this.handleAgentId(id);
      });

      await room.connect(serverUrl, participantToken);

      // Catch the active agent if its metadata was set before we subscribed.
      room.remoteParticipants.forEach((p) => {
        const id = this.extractAgentIdFromMetadata(p.metadata);
        if (id) this.handleAgentId(id);
      });

    } catch (err) {
      console.error("[nurixAdapter] web call error", err);
      const msg = err instanceof Error ? err.message : "Could not start the web call.";
      this.emit({ type: "error", message: msg });
    }
  }

  // ── Transcription WebSocket ────────────────────────────────────────────────

  private openTranscriptSocket(remoteCallId: string): void {
    const url    = `${this.wsBase}/voice/calls/${remoteCallId}/ws`;

    this.ws = new WebSocket(url);

    this.ws.onopen = () => {
      if (!this.alive) return;
      if (this.fallbackTimer) { clearTimeout(this.fallbackTimer); this.fallbackTimer = null; }
      this.emit({ type: "call.connected", startedAt: Date.now() });
    };

    this.ws.onmessage = (ev) => {
      if (!this.alive) return;
      const raw = typeof ev.data === "string" ? ev.data : "";
      try {
        this.handleServerMessage(JSON.parse(raw));
      } catch {
        const trimmed = raw.trim();
        if (trimmed) {
          this.emitTranscriptTurn({
            speaker:     "agent",
            text:        trimmed,
            timestampMs: Date.now(),
          });
        }
      }
    };

    this.ws.onerror = () => {
      if (!this.alive) return;
      this.emit({ type: "error", message: "Connection lost. Please try again." });
    };

    this.ws.onclose = (ev) => {
      if (!this.alive) return;
      if (ev.wasClean) {
        this.emit({ type: "call.ended", reason: "agent_hangup", endedAt: Date.now() });
      }
    };
  }

  // ── Server message router ──────────────────────────────────────────────────

  private handleServerMessage(msg: Record<string, unknown>, topic?: string): void {
    if (!msg || typeof msg !== "object") return;

    const toolCalls = Array.isArray(msg.tool_calls)
      ? msg.tool_calls
      : Array.isArray(msg.toolCalls)
        ? msg.toolCalls
        : null;

    if (toolCalls) {
      for (const item of toolCalls) {
        const event = asRecord(item) ? normalizeToolCallEvent(item as Record<string, unknown>, topic) : null;
        if (event) this.emit(event);
      }
      return;
    }

    // Standalone agents and squads do not always use the same envelope:
    // some publish `type: "tool_call"`, some send only `function_name`, and
    // some squads put the call under `payload`, `data`, or a `tool_call` topic.
    // Normalize those shapes before the transcript branch drops no-text frames.
    const toolCall = normalizeToolCallEvent(msg, topic);
    if (toolCall) {
      this.emit(toolCall);
      return;
    }

    // Untyped transcript frames — `{content|text, speaker}` with no `type`.
    // This is the live-transcription shape Voicex/NuPlay emits over LiveKit
    // data channels and the phone WS, and is what test-2's VoicexCallInterface
    // listens for too.
    if (!msg.type) {
      const text = (msg.content ?? msg.text ?? msg.transcript ?? "") as string;
      if (text) {
        this.emitTranscriptTurn({
          speaker:     normalizeSpeaker(msg.speaker),
          text,
          timestampMs: Date.now(),
        });
      }
      return;
    }

    switch (msg.type) {
      case "transcript":
      case "transcript.turn":
      case "transcription": {
        this.emitTranscriptTurn({
          speaker:     normalizeSpeaker(msg.speaker),
          text:        (msg.content ?? msg.text ?? msg.transcript ?? "") as string,
          timestampMs: Date.now(),
        });
        break;
      }
      case "call.ended":
      case "call_ended": {
        this.emit({ type: "call.ended", reason: "agent_hangup", endedAt: Date.now() });
        break;
      }
      case "agent.handoff":
      case "agent_handoff": {
        this.emit({
          type:             "agent.handoff",
          fromAgent:        (msg.from_agent ?? msg.fromAgent ?? "") as string,
          toAgent:          (msg.to_agent   ?? msg.toAgent   ?? "") as string,
          routingCondition: (msg.routing_condition ?? msg.routingCondition ?? "") as string,
          isWarmTransfer:   Boolean(msg.is_warm_transfer ?? msg.isWarmTransfer ?? false),
        });
        break;
      }
      case "live_action": {
        if (msg.action && typeof msg.action === "object") {
          this.emit({ type: "live_action", action: msg.action as never });
        }
        break;
      }
      case "tool_call": {
        // Live tool-call event (LiveToolCallEvent envelope from voiceX
        // TranscriptManager). Surface via a dedicated CallEvent — useCallEvents
        // turns this into a "tool" speaker TranscriptTurn for inline render.
        this.emit({
          type:         "tool_call",
          functionName: String(msg.function_name ?? ""),
          args:         (msg.args ?? {}) as Record<string, unknown>,
          output:       (msg.output ?? {}) as Record<string, unknown>,
          isError:      Boolean(msg.is_error),
          durationMs:   Number(msg.duration_ms ?? 0),
          timestampMs:  Date.now(),
        });
        break;
      }
    }
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  private emit(event: CallEvent): void {
    this.handlers.get(event.type)?.forEach((h) => h(event));
  }

  // Emit a transcript.turn as ONE bubble per spoken turn. The accumulator decides
  // whether this text starts a bubble, rewrites the open one, or adds nothing;
  // re-emitting a known id makes the reducer upsert rather than append.
  private emitTranscriptTurn(turn: { speaker: "agent" | "user" | "system"; text: string; timestampMs: number }): void {
    const next = this.turns.push(turn.speaker, turn.text, turn.timestampMs);
    if (!next) return;
    this.emit({ type: "transcript.turn", id: next.id, speaker: next.speaker, text: next.text, timestampMs: next.timestampMs });
  }

  // ── Active-agent tracking ────────────────────────────────────────────────

  // Load the crew's sub-agents (id → name) and edges (from→to → routing
  // condition) so handoff events can be rendered with friendly labels. Hits
  // the same agentX endpoint zero uses for the crew build canvas. Best-effort:
  // on failure, names fall back to raw agent ids.
  private async loadSquadDetail(): Promise<void> {
    try {
      const res = await fetch(
        `${this.apiBase}/squad/${encodeURIComponent(this.config.agentId)}/detail`,
        { headers: { accept: "application/json", "workspace-id": this.workspaceId } },
      );
      if (res.ok) {
        const json = await res.json();
        const data = (json?.data ?? json) as {
          agents?: { agent_id?: string; agent_name?: string }[];
          edges?: { start_node_agent_id?: string; end_node_agent_id?: string; transfer_condition?: string; transfer_message?: string }[];
        };
        for (const a of data?.agents ?? []) {
          if (a?.agent_id) this.agentNames.set(a.agent_id, a.agent_name || a.agent_id);
        }
        for (const e of data?.edges ?? []) {
          const from = e?.start_node_agent_id;
          const to = e?.end_node_agent_id;
          if (from && to) {
            this.edgeConditions.set(`${from}->${to}`, e?.transfer_condition || e?.transfer_message || "");
          }
        }
      }
    } catch {
      // Names will fall back to raw agent ids.
    } finally {
      this.squadDetailLoaded = true;
      const pending = this.pendingAgentIds;
      this.pendingAgentIds = [];
      for (const id of pending) this.handleAgentId(id);
    }
  }

  // Resolve a raw agent_id to a friendly handoff event. Dedupes consecutive
  // updates for the same agent and buffers until the squad detail has loaded
  // so the very first agent isn't rendered as a bare UUID.
  private handleAgentId(agentId: string): void {
    if (!this.alive || !agentId || agentId === this.lastAgentId) return;
    if (!this.squadDetailLoaded) {
      this.pendingAgentIds.push(agentId);
      return;
    }
    const fromId = this.lastAgentId;
    const toName = this.agentNames.get(agentId) || agentId;
    const fromName = fromId ? this.agentNames.get(fromId) || fromId : "";
    const condition = fromId ? this.edgeConditions.get(`${fromId}->${agentId}`) || "" : "";
    this.lastAgentId = agentId;
    this.emit({
      type: "agent.handoff",
      fromAgent: fromName,
      toAgent: toName,
      routingCondition: condition,
      isWarmTransfer: false,
    });
  }

  private extractAgentIdFromMetadata(raw: string | undefined): string | null {
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      const id = parsed?.agent_id;
      return typeof id === "string" && id.length > 0 ? id : null;
    } catch {
      return null;
    }
  }
}
